import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Button, Checkbox, HStack, Modal, ModalBody, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Spinner, Stack, Text, useColorModeValue, useToast, Input, Select,
} from '@chakra-ui/react';
import { FaCheckDouble } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { Proposal } from '../models/proposal';
import { WorkOrder } from '../models/workOrder';
import useCommissionVerification from '../hooks/useCommissionVerification';
import useSalesBibleActions from '../hooks/useSalesBibleActions';
import useProposalApproval from '../hooks/useProposalApproval';
import useCompanyDefaults from '../hooks/useCompanyDefaults';
import { finalInvoiceName } from '../utils/proposalNaming';
import useInvoicePlan from '../hooks/useInvoicePlan';
import InvoiceStepBanner from './InvoiceStepBanner';
import { money } from '../utils/money';
import { apiErrorMessage } from '../utils/apiError';

interface ProposalCompletionWizardProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal | undefined;
}

/**
 * Item 109: completing a proposal PROMPTS generating the FINAL invoice off the
 * SAME work order (the backend item-50 split makes the 2nd invoice the "final"
 * remaining-50%). Then the proposal is marked Complete. Or complete without
 * invoicing. Mirrors the approval wizard (which handles the deposit invoice).
 */
const ProposalCompletionWizard: React.FC<ProposalCompletionWizardProps> = ({ isOpen, onClose, proposal }) => {
  const toast = useToast();
  // item 343 — Sales Bible completeness GATE: every Bible field must be addressed
  // before the proposal may complete, so the board is always accurate.
  const { viewFor } = useCommissionVerification();
  const { save: saveBible } = useSalesBibleActions();
  const [gatePlatform, setGatePlatform] = useState('');
  const [gateIllustrations, setGateIllustrations] = useState('');
  const [savingBible, setSavingBible] = useState(false);
  const history = useHistory();
  const { getWorkOrderByProposal, generateDepositInvoice, completeProposal } = useProposalApproval();
  // item 485 — seed the itemize toggle from the company setting (default OFF)
  const { defaults: companyDefaults } = useCompanyDefaults();
  const defaultItemize = companyDefaults?.itemizeByDefault ?? false;
  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const [busy, setBusy] = useState(false);
  const [workOrder, setWorkOrder] = useState<WorkOrder | undefined>(undefined);
  const [itemize, setItemize] = useState(false); // Chad: default itemize OFF (TODO: make a company setting)

  // item 343 — what the Sales Bible still needs for THIS job (Chad: ALL fields
  // must be addressed — platform, illustrations, commissions placed — to close)
  // item 597 (REVERSES 570/578) — the proposal's OWN platform is the source now; a platform
  // set on the proposal (or entered here) satisfies the gate.
  const bibleMissing: string[] = [];
  if (proposal) {
    if (!(proposal.platform || gatePlatform)) bibleMissing.push('platform');
    if (!(proposal.illustrationsComplete || gateIllustrations)) bibleMissing.push('illustrations');
    if (viewFor(proposal.id).status === 'missing') bibleMissing.push('commissions');
  }

  // BUGFIX: render the gate INPUTS from the PERSISTED proposal only — NOT from the live
  // gate* state. Previously the inputs rendered on `bibleMissing.includes(...)`, which
  // recomputes to include gatePlatform/gateIllustrations; so the first keystroke made the
  // field "not missing" and immediately UNMOUNTED the input (the value vanished). These
  // stable flags keep the field mounted while you type; `bibleMissing` (live) still gates
  // the Complete button.
  const showPlatformField = proposal ? !proposal.platform : false;
  const showIllustrationsField = proposal ? !proposal.illustrationsComplete : false;
  const showCommissionsNote = proposal ? viewFor(proposal.id).status === 'missing' : false;
  const showBibleGate = showPlatformField || showIllustrationsField || showCommissionsNote;
  const stableMissing = [
    ...(showPlatformField ? ['platform'] : []),
    ...(showIllustrationsField ? ['illustrations'] : []),
    ...(showCommissionsNote ? ['commissions'] : []),
  ];

  const saveBibleFields = async () => {
    if (!proposal) return;
    setSavingBible(true);
    try {
      await saveBible(proposal.id, {
        platform: gatePlatform || proposal.platform || '',
        deploymentDate: (proposal as any).deploymentDate ?? '',
        illustrationsComplete: gateIllustrations || proposal.illustrationsComplete || '',
        projectLeadPayoutInput: proposal.projectLeadPayoutInput ?? '',
        notes: proposal.salesBibleNotes ?? '',
      });
      toast({ status: 'success', title: 'Sales Bible updated.' });
    } catch {
      toast({ status: 'error', title: 'Could not save the Sales Bible fields.' });
    } finally {
      setSavingBible(false);
    }
  };

  useEffect(() => {
    let alive = true;
    if (isOpen && proposal) {
      setBusy(false);
      setWorkOrder(undefined);
      setItemize(defaultItemize);
      getWorkOrderByProposal(proposal.id)
        .then((wo) => { if (alive) setWorkOrder(wo); })
        .catch((e) => {
          // don't fail silently — without the WO the final-invoice option can't work,
          // so tell the user instead of leaving the wizard quietly half-loaded.
          if (alive) {
            toast({
              status: 'error',
              title: 'Could not load the work order',
              description: apiErrorMessage(e),
            });
          }
        });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, proposal]);

  // item 1446 (Chad) — the loud step block: this is the FINAL (the balance) — and shout if the
  // deposit is still a Draft. Informational only.
  const { plan, isLoading: planLoading } = useInvoicePlan(workOrder?.id, isOpen && !!workOrder, null);

  if (!proposal) return null;

  const finalInvoiceNumber = finalInvoiceName(proposal);

  // item 706 (Chad) — a sold subscription (2yr/5yr) can't be completed until its recurring
  // billing is enrolled: completing it would strand the subscription with no live recurring
  // record. The proposal's index flag drives this; enroll from the invoice's RNWY panel first.
  const recurringGateBlocked = !!proposal.pendingRecurring;

  const complete = async (withInvoice: boolean) => {
    setBusy(true);
    try {
      let newInvoiceId: string | undefined;
      if (withInvoice && workOrder) {
        // (ii) WO closes automatically when the 2nd invoice is created (backend
        // item-50 split marks it InvoicesGenerated). (iii)/(iv) navigate to the
        // invoice form so the user can review, Finalize, and view the PDF.
        newInvoiceId = await generateDepositInvoice({
          // item 790 — blank = auto-number; the friendly name is the invoice NAME, not the number
          workOrderId: workOrder.id, invoiceNumber: '', itemize,
        });
      }
      await completeProposal(proposal.id);
      onClose();
      if (newInvoiceId) {
        // (iii) take the user straight to the invoice — they Finalize + PDF from there
        toast({
          status: 'success',
          title: plan ? `Final invoice created as a Draft — ${money(plan.billsNow)}. Proposal completed.` : 'Final invoice created — proposal completed.',
          description: plan && plan.drafts.length > 0
            ? `Heads up: ${plan.drafts.map((d) => d.invoiceNumber).filter(Boolean).join(', ')} ${plan.drafts.length === 1 ? 'is' : 'are'} still a Draft — finalize that one too.`
            : 'Finalize and send the invoice below.',
          duration: 9000,
          isClosable: true,
        });
        history.push(`/invoice/${newInvoiceId}`);
      } else {
        toast({ status: 'success', title: 'Proposal completed.' });
      }
    } catch {
      toast({ status: 'error', title: 'Could not complete the proposal.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!busy) onClose(); }} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>COMPLETE PROPOSAL</ModalHeader>
        <ModalBody>
          <Stack spacing={5}>
            <Text>
              Completing
              {' '}
              <strong>{proposal.proposalTitle}</strong>
              {' '}
              (
              {proposal.proposalNumber}
              ) generates the
              {' '}
              <strong>final invoice</strong>
              {' '}
              from its work order (the remaining balance), then closes the proposal.
            </Text>
            {workOrder && <InvoiceStepBanner plan={plan} isLoading={planLoading} />}
            <Stack bg={cardBg} borderRadius="md" p={4} spacing={2}>
              <HStack justify="space-between">
                <Text color="gray.500">Work order</Text>
                {workOrder
                  ? <Text fontWeight="medium">{workOrder.workOrderNumber || workOrder.workOrderTitle}</Text>
                  : <Spinner size="sm" />}
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.500">Final invoice</Text>
                <Text fontWeight="medium">{finalInvoiceNumber}</Text>
              </HStack>
            </Stack>
            <Checkbox isChecked={itemize} onChange={(e) => setItemize(e.target.checked)}>
              Itemize lines (group price)
            </Checkbox>
            {!workOrder && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                No work order found for this proposal — you can still complete it without a final invoice.
              </Alert>
            )}
            {/* item 706 (Chad) — subscription proposals can't complete until recurring is enrolled */}
            {recurringGateBlocked && (
              <Alert status="warning" borderRadius="md" fontSize="sm" alignItems="flex-start">
                <AlertIcon />
                <Text>
                  This proposal sold a
                  {' '}
                  <strong>recurring subscription</strong>
                  {' '}
                  that isn&apos;t enrolled yet (
                  <strong>Pending Recurring</strong>
                  ). Generate/finalize the invoice and enroll the subscription from its RNWY
                  panel first — then this proposal can be completed.
                </Text>
              </Alert>
            )}
            {/* item 343 — the Sales Bible gate: list what's missing + fill it inline.
                Render off the STABLE flags (persisted proposal) so inputs don't unmount as
                the user types; the heading lists the initially-missing set. */}
            {showBibleGate && (
              <Alert status="warning" borderRadius="md" fontSize="sm" alignItems="flex-start">
                <AlertIcon />
                <Stack spacing={2} w="full">
                  <Text fontWeight="semibold">
                    {`Sales Bible incomplete — address ${stableMissing.join(', ')} before completing (the board must stay accurate).`}
                  </Text>
                  {showPlatformField && (
                    <Input size="sm" placeholder="Platform" value={gatePlatform} onChange={(e) => setGatePlatform(e.target.value)} />
                  )}
                  {showIllustrationsField && (
                    <Select size="sm" placeholder="Illustrations complete?" value={gateIllustrations} onChange={(e) => setGateIllustrations(e.target.value)}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Select>
                  )}
                  {(showPlatformField || showIllustrationsField) && (
                    <Button size="sm" variant="outline" isLoading={savingBible} onClick={saveBibleFields}>
                      Save Sales Bible fields
                    </Button>
                  )}
                  {showCommissionsNote && (
                    <Text fontSize="xs" opacity={0.8}>
                      Commissions aren&apos;t placed for this job — flag the items / set the earner %s
                      (Settings → Build out commissions), then reopen this wizard.
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack w="full" spacing={2}>
            <Button
              leftIcon={<FaCheckDouble />}
              onClick={() => complete(true)}
              isLoading={busy}
              isDisabled={!workOrder || bibleMissing.length > 0 || recurringGateBlocked}
              w="full"
            >
              Generate final invoice &amp; complete
            </Button>
            <Button variant="ghost" size="sm" onClick={() => complete(false)} isDisabled={busy || bibleMissing.length > 0 || recurringGateBlocked} w="full">
              Complete without invoicing
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProposalCompletionWizard;
